/**
 * The IAB TCF stub: until the CMP (bc-tcf.js) loads, calls to __tcfapi are queued and "ping" answers, and
 * iframes can reach the API through the __tcfapiLocator frame and postMessage. Installed only when the
 * script tag asks for it (data-tcf="true"), so pages without the TCF do not look like they have a CMP.
 */
interface TcfMessage {
  __tcfapiCall?: {
    command: string;
    parameter?: unknown;
    version: number;
    callId: string | number;
  };
}

type TcfApi = (...args: unknown[]) => unknown;

export function installTcfStub(win: Window, doc: Document): void {
  const target = win as unknown as { __tcfapi?: TcfApi };
  if (typeof target.__tcfapi === "function") {
    return;
  }

  const queue: unknown[][] = [];
  target.__tcfapi = (...args: unknown[]) => {
    if (!args.length) {
      return queue;
    }
    if (args[0] === "ping" && typeof args[2] === "function") {
      (args[2] as (value: unknown, success: boolean) => void)(
        {
          gdprApplies: undefined,
          cmpLoaded: false,
          cmpStatus: "stub",
          displayStatus: "hidden",
          apiVersion: "2.2",
        },
        true,
      );
      return undefined;
    }
    queue.push(args);
    return undefined;
  };

  addLocator(doc);
  win.addEventListener("message", (event: MessageEvent) => {
    let data = event.data as TcfMessage | string;
    if (typeof data === "string") {
      try {
        data = JSON.parse(data) as TcfMessage;
      } catch {
        return;
      }
    }
    const call = (data as TcfMessage)?.__tcfapiCall;
    const source = event.source as Window | null;
    if (!call || !source || typeof target.__tcfapi !== "function") {
      return;
    }
    // Always the current __tcfapi: the stub until the CMP replaces it.
    target.__tcfapi(
      call.command,
      call.version,
      (returnValue: unknown, success: boolean) => {
        const reply = {
          __tcfapiReturn: { returnValue, success, callId: call.callId },
        };
        source.postMessage(
          typeof event.data === "string" ? JSON.stringify(reply) : reply,
          "*",
        );
      },
      call.parameter,
    );
  });
}

/** The frame vendors look for (by name) to find the CMP from inside iframes. */
function addLocator(doc: Document): void {
  if (doc.querySelector('iframe[name="__tcfapiLocator"]')) {
    return;
  }
  const add = () => {
    if (!doc.body) {
      return false;
    }
    const frame = doc.createElement("iframe");
    frame.name = "__tcfapiLocator";
    frame.title = "";
    frame.setAttribute("aria-hidden", "true");
    frame.style.display = "none";
    doc.body.appendChild(frame);
    return true;
  };
  if (!add()) {
    doc.addEventListener("DOMContentLoaded", () => add(), { once: true });
  }
}
